import { supabase, type Database } from '../lib/supabase'
import type { ScheduleEntry, CreateScheduleEntryRequest, UpdateScheduleEntryRequest } from '../types/schedule'
import type { SearchFilters } from '../types/search'
import { apiCall } from '../utils/networkErrorHandler'
import { logServiceError } from '../utils/errorLogger'
import { notificationSchedulerService } from './NotificationSchedulerService'

type ScheduleEntryRow = Database['public']['Tables']['schedule_entries']['Row']
type ScheduleEntryInsert = Database['public']['Tables']['schedule_entries']['Insert']
type ScheduleEntryUpdate = Database['public']['Tables']['schedule_entries']['Update']

export interface ConflictError {
  type: 'vehicle' | 'driver'
  conflictingEntry: ScheduleEntry
  message: string
}

export class ScheduleService {
  /**
   * Convert database row to ScheduleEntry interface
   */
  private mapRowToScheduleEntry(row: ScheduleEntryRow & {
    vehicles?: any,
    drivers?: any
  }): ScheduleEntry {
    return {
      id: row.id,
      startDate: new Date(row.start_date),
      endDate: new Date(row.end_date),
      isRange: row.is_range,
      startTime: row.start_time,
      endTime: row.end_time,
      vehicleId: row.vehicle_id,
      driverId: row.driver_id,
      notes: row.notes === null ? undefined : row.notes,
      vehicle: row.vehicles ? {
        id: row.vehicles.id,
        registrationNumber: row.vehicles.registration_number,
        vehicleType: row.vehicles.vehicle_type,
        brandModel: row.vehicles.brand_model,
        seatingCapacity: row.vehicles.seating_capacity,
        status: row.vehicles.status,
        imageUrl: row.vehicles.image_url,
        createdAt: new Date(row.vehicles.created_at),
        updatedAt: new Date(row.vehicles.updated_at)
      } : undefined,
      driver: row.drivers ? {
        id: row.drivers.id,
        fullName: row.drivers.full_name,
        phoneNumber: row.drivers.phone_number,
        status: row.drivers.status,
        lineUserId: row.drivers.line_user_id || undefined,
        avatarImage: row.drivers.avatar_url || undefined,
        color: row.drivers.color || undefined,
        createdAt: new Date(row.drivers.created_at),
        updatedAt: new Date(row.drivers.updated_at)
      } : undefined,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
      // Keep usageDate for backward compatibility during migration
      usageDate: row.usage_date ? new Date(row.usage_date) : new Date(row.start_date),
      // New fields
      timeSlot: row.time_slot || undefined,
      locations: row.locations || []
    }
  }

  /**
   * Convert ScheduleEntry interface to database insert format
   */
  private mapScheduleEntryToInsert(entry: CreateScheduleEntryRequest): ScheduleEntryInsert {
    // Ensure we use dates in local timezone, not UTC
    const localStartDate = new Date(entry.startDate.getTime() - entry.startDate.getTimezoneOffset() * 60000);
    const localEndDate = new Date(entry.endDate.getTime() - entry.endDate.getTimezoneOffset() * 60000);
    
    return {
      start_date: localStartDate.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
      end_date: localEndDate.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
      is_range: entry.isRange || (entry.startDate.getTime() !== entry.endDate.getTime()),
      start_time: entry.startTime,
      end_time: entry.endTime,
      vehicle_id: entry.vehicleId,
      driver_id: entry.driverId,
      notes: entry.notes !== undefined ? entry.notes : null,
      time_slot: entry.timeSlot || null,
      locations: entry.locations && entry.locations.length > 0 ? entry.locations : null
    }
  }

  /**
   * Convert ScheduleEntry interface to database update format
   */
  private mapScheduleEntryToUpdate(entry: UpdateScheduleEntryRequest): ScheduleEntryUpdate {
    const update: ScheduleEntryUpdate = {}
    
    if (entry.startDate !== undefined) {
      const localStartDate = new Date(entry.startDate.getTime() - entry.startDate.getTimezoneOffset() * 60000);
      update.start_date = localStartDate.toISOString().split('T')[0]
    }
    if (entry.endDate !== undefined) {
      const localEndDate = new Date(entry.endDate.getTime() - entry.endDate.getTimezoneOffset() * 60000);
      update.end_date = localEndDate.toISOString().split('T')[0]
    }
    if (entry.isRange !== undefined) {
      update.is_range = entry.isRange
    }
    if (entry.startTime !== undefined) {
      update.start_time = entry.startTime
    }
    if (entry.endTime !== undefined) {
      update.end_time = entry.endTime
    }
    if (entry.vehicleId !== undefined) {
      update.vehicle_id = entry.vehicleId
    }
    if (entry.driverId !== undefined) {
      update.driver_id = entry.driverId
    }
    if (entry.notes !== undefined) {
      update.notes = entry.notes
    }
    if (entry.timeSlot !== undefined) {
      update.time_slot = entry.timeSlot
    }
    if (entry.locations !== undefined) {
      update.locations = entry.locations && entry.locations.length > 0 ? entry.locations : null
    }
    
    return update
  }

  /**
   * Validate schedule entry data
   */
  private validateScheduleEntry(entry: CreateScheduleEntryRequest | UpdateScheduleEntryRequest): void {
    // Validate required fields for create operations
    if ('startDate' in entry && entry.startDate) {
      if (!(entry.startDate instanceof Date) || isNaN(entry.startDate.getTime())) {
        throw new Error('Start date must be a valid date')
      }
    }
    if ('endDate' in entry && entry.endDate) {
      if (!(entry.endDate instanceof Date) || isNaN(entry.endDate.getTime())) {
        throw new Error('End date must be a valid date')
      }
    }
    
    // Validate date range
    if ('startDate' in entry && 'endDate' in entry && entry.startDate && entry.endDate) {
      if (entry.endDate < entry.startDate) {
        throw new Error('End date must be after or equal to start date')
      }
    }

    if ('startTime' in entry && entry.startTime !== undefined) {
      if (!entry.startTime?.trim()) {
        throw new Error('Start time is required')
      }
      // Validate time format (HH:MM)
      if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(entry.startTime)) {
        throw new Error('Start time must be in HH:MM format')
      }
    }

    if ('endTime' in entry && entry.endTime !== undefined) {
      if (!entry.endTime?.trim()) {
        throw new Error('End time is required')
      }
      // Validate time format (HH:MM)
      if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(entry.endTime)) {
        throw new Error('End time must be in HH:MM format')
      }
    }

    if ('vehicleId' in entry && entry.vehicleId !== undefined) {
      if (!entry.vehicleId?.trim()) {
        throw new Error('Vehicle ID is required')
      }
    }

    if ('driverId' in entry && entry.driverId !== undefined) {
      if (!entry.driverId?.trim()) {
        throw new Error('Driver ID is required')
      }
    }

    // Validate that end time is after start time
    if ('startTime' in entry && 'endTime' in entry && entry.startTime && entry.endTime) {
      const startMinutes = this.timeToMinutes(entry.startTime)
      const endMinutes = this.timeToMinutes(entry.endTime)
      
      if (endMinutes <= startMinutes) {
        throw new Error('End time must be after start time')
      }
    }
  }

  /**
   * Convert time string (HH:MM or HH:MM:SS) to minutes since midnight
   */
  private timeToMinutes(time: string): number {
    const timeParts = time.split(':').map(Number)
    const hours = timeParts[0] || 0
    const minutes = timeParts[1] || 0
    // Ignore seconds if present
    return hours * 60 + minutes
  }

  /**
   * Check for time overlap between two time ranges
   */
  private hasTimeOverlap(
    start1: string, end1: string,
    start2: string, end2: string
  ): boolean {
    const start1Minutes = this.timeToMinutes(start1)
    const end1Minutes = this.timeToMinutes(end1)
    const start2Minutes = this.timeToMinutes(start2)
    const end2Minutes = this.timeToMinutes(end2)

    // Two ranges overlap if one starts before the other ends
    return start1Minutes < end2Minutes && start2Minutes < end1Minutes
  }

  /**
   * Create a new schedule entry
   * Requirements: 3.1, 3.2, 3.3 - Record usage with validation and immediate persistence
   */
  async createScheduleEntry(entryData: CreateScheduleEntryRequest): Promise<ScheduleEntry> {
    return apiCall(async () => {
      // Validate input data
      this.validateScheduleEntry(entryData)

      // Check for conflicts before creating
      await this.checkForConflicts(entryData)

      const insertData = this.mapScheduleEntryToInsert(entryData)
      
      const { data, error } = await supabase
        .from('schedule_entries')
        .insert(insertData)
        .select(`
          *,
          vehicles (*),
          drivers (*)
        `)
        .single()

      if (error) {
        // Handle foreign key constraint violations
        if (error.code === '23503') {
          if (error.message.includes('vehicle_id')) {
            const serviceError = new Error('Vehicle not found')
            logServiceError(serviceError, 'ScheduleService', 'createScheduleEntry', entryData)
            throw serviceError
          }
          if (error.message.includes('driver_id')) {
            const serviceError = new Error('Driver not found')
            logServiceError(serviceError, 'ScheduleService', 'createScheduleEntry', entryData)
            throw serviceError
          }
        }
        const serviceError = new Error(`Failed to create schedule entry: ${error.message}`)
        logServiceError(serviceError, 'ScheduleService', 'createScheduleEntry', entryData)
        throw serviceError
      }

      const scheduleEntry = this.mapRowToScheduleEntry(data)

      // Send assignment notification and schedule reminders
      console.log('🔔 [DEBUG] Attempting to send notifications for new schedule entry:', {
        scheduleId: scheduleEntry.id,
        driverId: scheduleEntry.driverId,
        driverName: scheduleEntry.driver?.fullName,
        usageDate: scheduleEntry.usageDate,
        startTime: scheduleEntry.startTime
      })
      
      try {
        console.log('🔔 [DEBUG] Calling scheduleAssignmentNotification...')
        await notificationSchedulerService.scheduleAssignmentNotification(scheduleEntry)
        console.log('🔔 [DEBUG] Assignment notification scheduled successfully')
        
        console.log('🔔 [DEBUG] Calling scheduleReminderNotifications...')
        await notificationSchedulerService.scheduleReminderNotifications(scheduleEntry)
        console.log('🔔 [DEBUG] Reminder notifications scheduled successfully')
      } catch (notificationError) {
        // Log notification error but don't fail the schedule creation
        console.error('❌ [DEBUG] Failed to schedule notifications:', notificationError)
        console.error('❌ [DEBUG] Full error details:', {
          message: notificationError instanceof Error ? notificationError.message : String(notificationError),
          stack: notificationError instanceof Error ? notificationError.stack : undefined
        })
      }

      return scheduleEntry
    }, 'createScheduleEntry')
  }

  /**
   * Get schedule entry by ID
   */
  async getScheduleEntryById(id: string): Promise<ScheduleEntry | null> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('schedule_entries')
        .select(`
          *,
          vehicles (*),
          drivers (*)
        `)
        .eq('id', id)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Schedule entry not found
        }
        const serviceError = new Error(`Failed to get schedule entry: ${error.message}`)
        logServiceError(serviceError, 'ScheduleService', 'getScheduleEntryById', { id })
        throw serviceError
      }

      return this.mapRowToScheduleEntry(data)
    }, 'getScheduleEntryById')
  }

  /**
   * Get all schedule entries
   */
  async getAllScheduleEntries(): Promise<ScheduleEntry[]> {
    return apiCall(async () => {
      const { data, error } = await supabase
        .from('schedule_entries')
        .select(`
          *,
          vehicles (*),
          drivers (*)
        `)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })

      if (error) {
        const serviceError = new Error(`Failed to get schedule entries: ${error.message}`)
        logServiceError(serviceError, 'ScheduleService', 'getAllScheduleEntries')
        throw serviceError
      }

      return data.map((row: any) => this.mapRowToScheduleEntry(row))
    }, 'getAllScheduleEntries')
  }

  /**
   * Update schedule entry
   * Requirements: 3.4 - Allow modification while maintaining data integrity
   */
  async updateScheduleEntry(id: string, entryData: UpdateScheduleEntryRequest): Promise<ScheduleEntry> {
    // Validate input data
    this.validateScheduleEntry(entryData)

    // Get current entry to check for conflicts
    const currentEntry = await this.getScheduleEntryById(id)
    if (!currentEntry) {
      throw new Error('Schedule entry not found')
    }

    // Create a merged entry for conflict checking
    const mergedEntry: CreateScheduleEntryRequest = {
      startDate: entryData.startDate ?? currentEntry.startDate,
      endDate: entryData.endDate ?? currentEntry.endDate,
      isRange: entryData.isRange ?? currentEntry.isRange,
      startTime: entryData.startTime ?? currentEntry.startTime,
      endTime: entryData.endTime ?? currentEntry.endTime,
      vehicleId: entryData.vehicleId ?? currentEntry.vehicleId,
      driverId: entryData.driverId ?? currentEntry.driverId,
      notes: entryData.notes ?? currentEntry.notes
    }

    // Check for conflicts (excluding current entry)
    await this.checkForConflicts(mergedEntry, id)

    const updateData = this.mapScheduleEntryToUpdate(entryData)
    
    const { data, error } = await supabase
      .from('schedule_entries')
      .update(updateData)
      .eq('id', id)
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      .single()

    if (error) {
      // Handle foreign key constraint violations
      if (error.code === '23503') {
        if (error.message.includes('vehicle_id')) {
          throw new Error('Vehicle not found')
        }
        if (error.message.includes('driver_id')) {
          throw new Error('Driver not found')
        }
      }
      if (error.code === 'PGRST116') {
        throw new Error('Schedule entry not found')
      }
      throw new Error(`Failed to update schedule entry: ${error.message}`)
    }

    const updatedScheduleEntry = this.mapRowToScheduleEntry(data)

    // Send update notification
    console.log('🔔 [DEBUG] Attempting to send update notification:', {
      scheduleId: updatedScheduleEntry.id,
      driverId: updatedScheduleEntry.driverId,
      oldDriverId: currentEntry.driverId,
      driverName: updatedScheduleEntry.driver?.fullName
    })
    
    try {
      const changes = this.detectChanges(currentEntry, updatedScheduleEntry)
      console.log('🔔 [DEBUG] Detected changes:', changes)
      
      if (changes.length > 0) {
        console.log('🔔 [DEBUG] Calling sendUpdateNotification...')
        await notificationSchedulerService.sendUpdateNotification(
          currentEntry,
          updatedScheduleEntry,
          changes
        )
        console.log('🔔 [DEBUG] Update notification sent successfully')
      } else {
        console.log('🔔 [DEBUG] No changes detected, skipping notification')
      }
    } catch (notificationError) {
      // Log notification error but don't fail the update
      console.error('❌ [DEBUG] Failed to send update notification:', notificationError)
      console.error('❌ [DEBUG] Full error details:', {
        message: notificationError instanceof Error ? notificationError.message : String(notificationError),
        stack: notificationError instanceof Error ? notificationError.stack : undefined
      })
    }

    return updatedScheduleEntry
  }

  /**
   * Delete schedule entry
   * Requirements: 3.5 - Support deletion with proper confirmation
   */
  async deleteScheduleEntry(id: string): Promise<void> {
    // Get the schedule entry before deletion for notification
    const scheduleEntry = await this.getScheduleEntryById(id)

    const { error } = await supabase
      .from('schedule_entries')
      .delete()
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to delete schedule entry: ${error.message}`)
    }

    // Send cancellation notification
    if (scheduleEntry) {
      console.log('🔔 [DEBUG] Attempting to send cancellation notification:', {
        scheduleId: scheduleEntry.id,
        driverId: scheduleEntry.driverId,
        driverName: scheduleEntry.driver?.fullName,
        usageDate: scheduleEntry.usageDate,
        startTime: scheduleEntry.startTime
      })
      
      try {
        console.log('🔔 [DEBUG] Calling sendCancellationNotification...')
        await notificationSchedulerService.sendCancellationNotification(
          scheduleEntry,
          'งานถูกยกเลิก'
        )
        console.log('🔔 [DEBUG] Cancellation notification sent successfully')
      } catch (notificationError) {
        // Log notification error but don't fail the deletion
        console.error('❌ [DEBUG] Failed to send cancellation notification:', notificationError)
        console.error('❌ [DEBUG] Full error details:', {
          message: notificationError instanceof Error ? notificationError.message : String(notificationError),
          stack: notificationError instanceof Error ? notificationError.stack : undefined
        })
      }
    } else {
      console.warn('⚠️ [DEBUG] No schedule entry found for deletion, cannot send cancellation notification')
    }
  }

  /**
   * Get schedule entries by date range
   * Requirements: 3.1 - Add schedule querying by date ranges
   */
  async getScheduleEntriesByDateRange(startDate: Date, endDate: Date): Promise<ScheduleEntry[]> {
    const localStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
    const localEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
    const startDateStr = localStartDate.toISOString().split('T')[0]
    const endDateStr = localEndDate.toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      // Date ranges overlap: entry starts before or on our end date AND entry ends after or on our start date
      .lte('start_date', endDateStr)
      .gte('end_date', startDateStr)
      .order('start_date', { ascending: true })
      .order('start_time', { ascending: true })

    if (error) {
      throw new Error(`Failed to get schedule entries by date range: ${error.message}`)
    }

    return data.map((row: any) => this.mapRowToScheduleEntry(row))
  }

  /**
   * Get schedule entries by vehicle
   */
  async getScheduleEntriesByVehicle(vehicleId: string, startDate?: Date, endDate?: Date): Promise<ScheduleEntry[]> {
    let query = supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      .eq('vehicle_id', vehicleId)

    if (startDate && endDate) {
      const localStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
      const localEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
      const startDateStr = localStartDate.toISOString().split('T')[0]
      const endDateStr = localEndDate.toISOString().split('T')[0]
      // Date ranges overlap: entry starts before or on our end date AND entry ends after or on our start date
      query = query.lte('start_date', endDateStr).gte('end_date', startDateStr)
    } else if (startDate) {
      const localStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
      query = query.gte('end_date', localStartDate.toISOString().split('T')[0])
    } else if (endDate) {
      const localEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
      query = query.lte('start_date', localEndDate.toISOString().split('T')[0])
    }

    query = query.order('start_date', { ascending: true })
      .order('start_time', { ascending: true })

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to get schedule entries by vehicle: ${error.message}`)
    }

    return data.map((row: any) => this.mapRowToScheduleEntry(row))
  }

  /**
   * Get schedule entries by driver
   */
  async getScheduleEntriesByDriver(driverId: string, startDate?: Date, endDate?: Date): Promise<ScheduleEntry[]> {
    let query = supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      .eq('driver_id', driverId)

    if (startDate && endDate) {
      const localStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
      const localEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
      const startDateStr = localStartDate.toISOString().split('T')[0]
      const endDateStr = localEndDate.toISOString().split('T')[0]
      // Date ranges overlap: entry starts before or on our end date AND entry ends after or on our start date
      query = query.lte('start_date', endDateStr).gte('end_date', startDateStr)
    } else if (startDate) {
      const localStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
      query = query.gte('end_date', localStartDate.toISOString().split('T')[0])
    } else if (endDate) {
      const localEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
      query = query.lte('start_date', localEndDate.toISOString().split('T')[0])
    }

    query = query.order('start_date', { ascending: true })
      .order('start_time', { ascending: true })

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to get schedule entries by driver: ${error.message}`)
    }

    return data.map((row: any) => this.mapRowToScheduleEntry(row))
  }

  /**
   * Check for scheduling conflicts
   * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5 - Prevent double-booking and detect conflicts
   */
  async checkForConflicts(entry: CreateScheduleEntryRequest, excludeId?: string): Promise<void> {
    const localStartDate = new Date(entry.startDate.getTime() - entry.startDate.getTimezoneOffset() * 60000);
    const localEndDate = new Date(entry.endDate.getTime() - entry.endDate.getTimezoneOffset() * 60000);
    const startDateStr = localStartDate.toISOString().split('T')[0]
    const endDateStr = localEndDate.toISOString().split('T')[0]

    // Check for vehicle conflicts with date range overlap
    let vehicleQuery = supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      .eq('vehicle_id', entry.vehicleId)
      .lte('start_date', endDateStr) // Entry starts before or on our end date
      .gte('end_date', startDateStr)  // Entry ends after or on our start date

    if (excludeId) {
      vehicleQuery = vehicleQuery.neq('id', excludeId)
    }

    const { data: vehicleConflicts, error: vehicleError } = await vehicleQuery

    if (vehicleError) {
      throw new Error(`Failed to check vehicle conflicts: ${vehicleError.message}`)
    }

    // Check for time overlaps with vehicle conflicts
    for (const conflict of vehicleConflicts) {
      if (this.hasTimeOverlap(entry.startTime, entry.endTime, conflict.start_time, conflict.end_time)) {
        const conflictEntry = this.mapRowToScheduleEntry(conflict)
        const startDateThai = this.formatDateStringToThai(conflict.start_date)
        const endDateThai = this.formatDateStringToThai(conflict.end_date)
        const startTime = this.formatTime(conflict.start_time)
        const endTime = this.formatTime(conflict.end_time)

        const conflictDateRange = conflict.start_date === conflict.end_date
          ? `วันที่ ${startDateThai}`
          : `ตั้งแต่วันที่ ${startDateThai} ถึง ${endDateThai}`
        throw new Error(`ตรวจพบความขัดแย้ง: รถยนต์มีการจองแล้ว${conflictDateRange} เวลา ${startTime} - ${endTime}`)
      }
    }

    // Check for driver conflicts with date range overlap
    let driverQuery = supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)
      .eq('driver_id', entry.driverId)
      .lte('start_date', endDateStr) // Entry starts before or on our end date
      .gte('end_date', startDateStr)  // Entry ends after or on our start date

    if (excludeId) {
      driverQuery = driverQuery.neq('id', excludeId)
    }

    const { data: driverConflicts, error: driverError } = await driverQuery

    if (driverError) {
      throw new Error(`Failed to check driver conflicts: ${driverError.message}`)
    }

    // Check for time overlaps with driver conflicts
    for (const conflict of driverConflicts) {
      if (this.hasTimeOverlap(entry.startTime, entry.endTime, conflict.start_time, conflict.end_time)) {
        const conflictEntry = this.mapRowToScheduleEntry(conflict)
        const startDateThai = this.formatDateStringToThai(conflict.start_date)
        const endDateThai = this.formatDateStringToThai(conflict.end_date)
        const startTime = this.formatTime(conflict.start_time)
        const endTime = this.formatTime(conflict.end_time)

        const conflictDateRange = conflict.start_date === conflict.end_date
          ? `วันที่ ${startDateThai}`
          : `ตั้งแต่วันที่ ${startDateThai} ถึง ${endDateThai}`
        throw new Error(`ตรวจพบความขัดแย้ง: คนขับมีการจองแล้ว${conflictDateRange} เวลา ${startTime} - ${endTime}`)
      }
    }
  }

  /**
   * Search and filter schedule entries
   */
  async searchScheduleEntries(filters: SearchFilters): Promise<ScheduleEntry[]> {
    let query = supabase
      .from('schedule_entries')
      .select(`
        *,
        vehicles (*),
        drivers (*)
      `)

    // Apply vehicle filter
    if (filters.vehicleId?.trim()) {
      query = query.eq('vehicle_id', filters.vehicleId.trim())
    }

    // Apply driver filter
    if (filters.driverId?.trim()) {
      query = query.eq('driver_id', filters.driverId.trim())
    }

    // Apply date range filters
    if (filters.startDate && filters.endDate) {
      const localStartDate = new Date(filters.startDate.getTime() - filters.startDate.getTimezoneOffset() * 60000);
      const localEndDate = new Date(filters.endDate.getTime() - filters.endDate.getTimezoneOffset() * 60000);
      const startDateStr = localStartDate.toISOString().split('T')[0]
      const endDateStr = localEndDate.toISOString().split('T')[0]
      // Date ranges overlap: entry starts before or on our end date AND entry ends after or on our start date
      query = query.lte('start_date', endDateStr).gte('end_date', startDateStr)
    } else if (filters.startDate) {
      const localStartDate = new Date(filters.startDate.getTime() - filters.startDate.getTimezoneOffset() * 60000);
      query = query.gte('end_date', localStartDate.toISOString().split('T')[0])
    } else if (filters.endDate) {
      const localEndDate = new Date(filters.endDate.getTime() - filters.endDate.getTimezoneOffset() * 60000);
      query = query.lte('start_date', localEndDate.toISOString().split('T')[0])
    }

    query = query.order('start_date', { ascending: true })
      .order('start_time', { ascending: true })

    const { data, error } = await query

    if (error) {
      throw new Error(`Failed to search schedule entries: ${error.message}`)
    }

    return data.map((row: any) => this.mapRowToScheduleEntry(row))
  }

  /**
   * Detect changes between old and new schedule entries
   */
  private detectChanges(oldSchedule: ScheduleEntry, newSchedule: ScheduleEntry): string[] {
    const changes: string[] = []

    if (oldSchedule.startDate.getTime() !== newSchedule.startDate.getTime() || oldSchedule.endDate.getTime() !== newSchedule.endDate.getTime()) {
      const oldDateRange = oldSchedule.isRange
        ? `จาก ${this.formatThaiDate(oldSchedule.startDate)} ถึง ${this.formatThaiDate(oldSchedule.endDate)}`
        : `วันที่ ${this.formatThaiDate(oldSchedule.startDate)}`
      const newDateRange = newSchedule.isRange
        ? `จาก ${this.formatThaiDate(newSchedule.startDate)} ถึง ${this.formatThaiDate(newSchedule.endDate)}`
        : `วันที่ ${this.formatThaiDate(newSchedule.startDate)}`
      changes.push(`ช่วงวันที่เปลี่ยน${oldDateRange} เป็น${newDateRange}`)
    }

    if (oldSchedule.startTime !== newSchedule.startTime) {
      changes.push(`เวลาเริ่มต้นเปลี่ยนจาก ${oldSchedule.startTime} เป็น ${newSchedule.startTime}`)
    }

    if (oldSchedule.endTime !== newSchedule.endTime) {
      changes.push(`เวลาสิ้นสุดเปลี่ยนจาก ${oldSchedule.endTime} เป็น ${newSchedule.endTime}`)
    }

    if (oldSchedule.vehicleId !== newSchedule.vehicleId) {
      const oldVehicle = oldSchedule.vehicle?.registrationNumber || 'ไม่ระบุ'
      const newVehicle = newSchedule.vehicle?.registrationNumber || 'ไม่ระบุ'
      changes.push(`รถเปลี่ยนจาก ${oldVehicle} เป็น ${newVehicle}`)
    }

    if (oldSchedule.notes !== newSchedule.notes) {
      changes.push('หมายเหตุมีการเปลี่ยนแปลง')
    }

    return changes
  }

  /**
   * Format date to Thai format
   */
  private formatThaiDate(date: Date): string {
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]

    const day = date.getDate()
    const month = thaiMonths[date.getMonth()]
    const year = date.getFullYear() + 543

    return `${day} ${month} ${year}`
  }

  /**
   * Format date string (YYYY-MM-DD) to Thai format
   */
  private formatDateStringToThai(dateString: string): string {
    const [year, month, day] = dateString.split('-').map(Number)
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]

    const thaiYear = year + 543
    const monthName = thaiMonths[month - 1]

    return `${day} ${monthName} ${thaiYear}`
  }

  /**
   * Format time (HH:MM:SS or HH:MM) to Thai time format (HH:MM)
   */
  private formatTime(time: string): string {
    // Remove seconds if present (HH:MM:SS -> HH:MM)
    return time.substring(0, 5)
  }
}

// Export singleton instance
export const scheduleService = new ScheduleService()
